/**
 * State Senate / State House district pages (sites#471): the renderer's page (us-pages, pages.elect.info
 * district.tsx renderStateLegislative) mounts `#sld-map` and `#sld-related` with the district's state, layer and code.
 * This script fetches the state's two TIGER layers (the files the /districts/ map uses) and adds:
 *  - an interactive map: this district highlighted, the chamber's other districts clickable;
 *  - the other chamber: the districts this one lies within or overlaps (with the share) and the ones inside it —
 *    "Within State Senate District 1" for MN 1A, "Same boundary as" where both chambers share districts (AZ, WA);
 *  - neighbouring districts of the chamber and previous / next.
 * Without JavaScript the page keeps its static SVG boundary and the server-rendered links.
 */
import { sldDisplayName, sldSlug, SLD_LABEL, type SLDKind } from './district-labels';
import { loadLeaflet } from './leaflet-loader';
import { compareCodes, NESTED_SHARE, neighbours, overlaps, toShape, type Shape } from './sld-geometry';

declare const L: any;

export interface Feature { type: string; properties: Record<string, any>; geometry: any }

export interface DistrictInfo { kind: SLDKind; code: string; name: string; href: string | null }

export interface Relations {
  /** Other-chamber districts this one lies in, largest share of this district first. */
  within: Array<DistrictInfo & { share: number }>;
  /** Other-chamber districts lying (≥ NESTED_SHARE) inside this one. */
  contains: DistrictInfo[];
  /** Other-chamber districts with the same boundary (each ≥ NESTED_SHARE inside the other). */
  same: DistrictInfo[];
  neighbours: DistrictInfo[];
  previous: DistrictInfo | null;
  next: DistrictInfo | null;
}

/** Jurisdictions TIGER publishes no lower-house layer for: Nebraska is unicameral, D.C. has its council wards only. */
export const NO_LOWER_HOUSE = new Set(['NE', 'DC']);

export function noOtherChamber(state: string, otherLabel: string): string {
  if (state === 'NE') return 'Nebraska has a one-house (unicameral) legislature.';
  if (state === 'DC') return 'The District of Columbia has a council, not a two-chamber legislature; its wards are shown here.';
  return `No ${otherLabel} boundaries are published for this state.`;
}

const other = (kind: SLDKind): SLDKind => (kind === 'sldu' ? 'sldl' : 'sldu');

export function info(kind: SLDKind, state: string, props: Record<string, any>): DistrictInfo {
  const code = String(props.code || '');
  const slug = sldSlug(state, kind, code);
  return { kind, code, name: sldDisplayName('', kind, code, props.name), href: slug ? `/districts/${slug}/` : null };
}

function shapesOf(features: Feature[]): Map<string, { shape: Shape; props: Record<string, any> }> {
  const out = new Map<string, { shape: Shape; props: Record<string, any> }>();
  for (const f of features) {
    const code = String(f.properties?.code || '').toUpperCase();
    if (!code || code === 'ZZZ') continue;
    const shape = toShape(code, f.geometry);
    if (shape) out.set(code, { shape, props: f.properties });
  }
  return out;
}

/** Everything the related-districts panel shows, from the two layers' features. */
export function relations(state: string, kind: SLDKind, code: string, same: Feature[], others: Feature[]): Relations | null {
  const mine = shapesOf(same);
  const self = mine.get(code.toUpperCase());
  if (!self) return null;
  const theirs = shapesOf(others);
  const otherShapes = [...theirs.values()].map(t => t.shape);
  const otherKind = other(kind);
  const describe = (k: SLDKind, c: string, table: Map<string, { props: Record<string, any> }>) => info(k, state, table.get(c)!.props);

  const withinRaw = overlaps(self.shape, otherShapes);
  const containsRaw = otherShapes
    .filter(o => o.bbox[0] <= self.shape.bbox[2] && self.shape.bbox[0] <= o.bbox[2] && o.bbox[1] <= self.shape.bbox[3] && self.shape.bbox[1] <= o.bbox[3])
    .map(o => ({ key: o.key, share: overlaps(o, [self.shape], NESTED_SHARE)[0]?.share ?? 0 }))
    .filter(o => o.share >= NESTED_SHARE);
  const containsKeys = new Set(containsRaw.map(o => o.key));
  const sameKeys = new Set(withinRaw.filter(o => o.share >= NESTED_SHARE && containsKeys.has(o.key)).map(o => o.key));

  const order = [...mine.keys()].sort(compareCodes);
  const at = order.indexOf(self.shape.key);
  const neighbourKeys = neighbours(self.shape, [...mine.values()].map(m => m.shape)).sort(compareCodes);
  return {
    // A district wholly inside this one is listed as such, not again as an overlap.
    within: withinRaw.filter(o => !sameKeys.has(o.key) && !containsKeys.has(o.key)).map(o => ({ ...describe(otherKind, o.key, theirs), share: o.share })),
    contains: containsRaw.filter(o => !sameKeys.has(o.key)).map(o => o.key).sort(compareCodes).map(c => describe(otherKind, c, theirs)),
    same: [...sameKeys].sort(compareCodes).map(c => describe(otherKind, c, theirs)),
    neighbours: neighbourKeys.map(c => describe(kind, c, mine)),
    previous: at > 0 ? describe(kind, order[at - 1], mine) : null,
    next: at >= 0 && at < order.length - 1 ? describe(kind, order[at + 1], mine) : null,
  };
}

/** "62%" (whole percent; "<1%" and ">99%" at the ends). */
export function percent(share: number): string {
  if (share > 0.99 && share < 1) return '>99%';
  if (share < 0.01) return '<1%';
  return `${Math.round(share * 100)}%`;
}

// ---------------------------------------------------------------------------
// DOM (browser only)

function el(tag: string, attrs: Record<string, string> = {}, text?: string): HTMLElement {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  if (text !== undefined) node.textContent = text;
  return node;
}

function link(d: DistrictInfo, suffix = ''): HTMLElement {
  const label = d.name + suffix;
  return d.href ? el('a', { href: d.href, 'data-code': d.code }, label) : el('span', { 'data-code': d.code }, label);
}

function list(title: string, items: HTMLElement[], id: string): HTMLElement | null {
  if (!items.length) return null;
  const block = el('div', { class: 'sld-related-block', 'data-related': id });
  block.appendChild(el('h3', {}, title));
  const ul = el('ul');
  for (const item of items) { const li = el('li'); li.appendChild(item); ul.appendChild(li); }
  block.appendChild(ul);
  return block;
}

export function renderRelations(container: HTMLElement, kind: SLDKind, r: Relations, hasOtherLayer: boolean, state = ''): void {
  const otherLabel = SLD_LABEL[other(kind)];
  const blocks: Array<HTMLElement | null> = [];
  if (r.same.length) blocks.push(list(`Same boundary as`, r.same.map(d => link(d)), 'same'));
  const nested = r.within.length > 0 && r.within[0].share >= NESTED_SHARE;
  if (nested) blocks.push(list('Within', [link(r.within[0])], 'within'));
  else blocks.push(list(`Overlapping ${otherLabel} districts`, r.within.map(d => link(d, ` (${percent(d.share)} of this district)`)), 'within'));
  blocks.push(list(`${otherLabel} districts within this one`, r.contains.map(d => link(d)), 'contains'));
  blocks.push(list(`Neighbouring ${SLD_LABEL[kind]} districts`, r.neighbours.map(d => link(d)), 'neighbours'));
  const nav = el('p', { class: 'sld-prev-next', 'data-related': 'prev-next' });
  if (r.previous) { nav.appendChild(document.createTextNode('← ')); nav.appendChild(link(r.previous)); }
  if (r.previous && r.next) nav.appendChild(document.createTextNode(' · '));
  if (r.next) { nav.appendChild(link(r.next)); nav.appendChild(document.createTextNode(' →')); }
  if (r.previous || r.next) blocks.push(nav);
  if (!hasOtherLayer) blocks.push(el('p', { class: 'sld-note' }, noOtherChamber(state, otherLabel)));
  for (const b of blocks) if (b) container.appendChild(b);
  container.dataset.loaded = 'true';
}

function drawMap(mount: HTMLElement, state: string, kind: SLDKind, code: string, features: Feature[]): void {
  const frame = el('div', { id: 'sld-leaflet', class: 'sld-leaflet' });
  mount.prepend(frame);
  mount.classList.add('sld-map-live');
  const map = L.map(frame, { scrollWheelZoom: false, maxZoom: 14 });
  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  }).addTo(map);
  let selfLayer: any = null;
  const layer = L.geoJSON({ type: 'FeatureCollection', features }, {
    style: (f: any) => (String(f.properties.code).toUpperCase() === code
      ? { color: '#1a365d', weight: 3, fillColor: '#ed8936', fillOpacity: 0.5 }
      : { color: '#2c5282', weight: 1, fillColor: '#e2e8f0', fillOpacity: 0.25 }),
    onEachFeature: (f: any, l: any) => {
      const d = info(kind, state, f.properties);
      const isSelf = d.code.toUpperCase() === code;
      if (isSelf) selfLayer = l;
      l.bindTooltip(isSelf ? `${d.name} (this district)` : d.name, { sticky: true, direction: 'top' });
      l.on('add', function (this: any) {
        const p = this.getElement();
        if (!p) return;
        p.setAttribute('data-code', d.code);
        if (!isSelf && d.href) p.setAttribute('data-href', d.href);
      });
      if (!isSelf && d.href) l.on('click', () => { window.location.href = d.href!; });
    },
  }).addTo(map);
  map.fitBounds((selfLayer || layer).getBounds(), { padding: [40, 40] });
  if (selfLayer) selfLayer.bringToFront();
  mount.dataset.loaded = 'true';
}

async function setup(): Promise<void> {
  const mapMount = document.getElementById('sld-map');
  const related = document.getElementById('sld-related');
  const source = mapMount || related;
  if (!source) return;
  const state = String(source.dataset.state || '').toUpperCase();
  const kind = source.dataset.layer as SLDKind;
  const code = String(source.dataset.code || '').toUpperCase();
  if (!/^[A-Z]{2}$/.test(state) || (kind !== 'sldu' && kind !== 'sldl') || !code) return;
  const fetchLayer = (k: SLDKind) => (NO_LOWER_HOUSE.has(state) && k === 'sldl' ? Promise.resolve(null) : fetch(`/geo/${k}/us-${k}-${state.toLowerCase()}.geojson`)
    .then(r => (r.ok ? r.json() : null)).then(d => (d?.features as Feature[]) || null).catch(() => null));
  const [same, others] = await Promise.all([fetchLayer(kind), fetchLayer(other(kind))]);
  if (!same) return;
  if (mapMount) {
    loadLeaflet().then(() => drawMap(mapMount, state, kind, code, same)).catch(err => console.warn('District map unavailable:', err));
  }
  if (related) {
    // Sampling a large state (Alaska's Aleutians) can take a moment: after the map, off the first paint.
    setTimeout(() => {
      const r = relations(state, kind, code, same, others || []);
      if (r) renderRelations(related, kind, r, others !== null, state);
    }, 0);
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { setup(); });
  else setup();
}
